import {
    CheckCircleOutlined, SearchOutlined,
} from '@ant-design/icons';
import {
    useMutation, useQuery,
} from '@tanstack/react-query';
import {
    Card, Checkbox, Empty, Flex, Input, Space, Spin, Tag,
} from 'antd';
import {
    forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState,
} from 'react';

import {
    projectsQueries,
    usersMutations,
    usersQueries,
} from '@/api';

type UserProjectsFormProps = {
    userId: string;
    onSave?: () => void;
};

export type UserProjectsFormRef = {
    submit: () => void;
    getFormData?: () => { selectedProjects: string[]; } | null;
};

export const UserProjectsForm = forwardRef<UserProjectsFormRef, UserProjectsFormProps>(({ userId, onSave }, ref) => {
    const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
    const [searchText, setSearchText] = useState('');

    const { data: user, isLoading: isLoadingUser } = useQuery(
        usersQueries.getOneUser(userId),
    );

    const { data: projectsData, isLoading: isLoadingProjects } = useQuery(
        projectsQueries.getAllProjects({
            order_column: 'title',
            order_direction: 'asc',
            per_page: -1,
            current_page: 1,
            search: '',
        }),
    );

    const syncAccessMutationOptions = useMemo(
        () => usersMutations.syncAccess(userId),
        [userId],
    );
    const { mutate: syncAccess, isPending: isSyncing } = useMutation(syncAccessMutationOptions);

    useEffect(() => {
        if (user) {
            setSelectedProjects(user.projects || []);
            setSearchText('');
        }
    }, [user]);

    const handleProjectChange = useCallback((projectId: string, checked: boolean) => {
        if (checked) {
            setSelectedProjects((prev) => [...prev, projectId]);
        } else {
            setSelectedProjects((prev) => prev.filter((id) => id !== projectId));
        }
    }, []);

    const handleSubmit = useCallback(() => {
        syncAccess(
            {
                project_ids: selectedProjects,
            },
            {
                onSuccess: () => {
                    onSave?.();
                },
            },
        );
    }, [selectedProjects, syncAccess, onSave]);

    useImperativeHandle(ref, () => ({
        submit: handleSubmit,
        getFormData: () => ({
            selectedProjects,
        }),
    }), [handleSubmit, selectedProjects]);

    const filteredProjects = useMemo(() => {
        if (!projectsData?.items) {
            return [];
        }

        return projectsData.items.filter(
            (project) =>
                project.title.toLowerCase().includes(searchText.toLowerCase()) ||
                project.scripts?.some(
                    (script) =>
                        script.title.toLowerCase().includes(searchText.toLowerCase()) ||
                        (script.product_sku ?? '').toLowerCase().includes(searchText.toLowerCase()),
                ),
        ).sort((a, b) => selectedProjects.includes(a.id) ? -1 : selectedProjects.includes(b.id) ? 1 : 0);
    }, [projectsData?.items, searchText, selectedProjects]);

    const isLoading = isLoadingUser || isLoadingProjects;

    if (isLoading) {
        return (
            <Flex justify="center" align="center" style={{ padding: 40 }}>
                <Spin size="large" />
            </Flex>
        );
    }

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Flex align="center" gap={16}>
                <Input
                    placeholder="Пошук по проектах"
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 400 }}
                    allowClear
                    disabled={isSyncing}
                />
                <Space>
                    <Tag color="blue" icon={<CheckCircleOutlined />}>
                        Вибрано проектів: {selectedProjects.length}
                    </Tag>
                </Space>
            </Flex>

            <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    {filteredProjects.map((project) => {
                        const isProjectSelected = selectedProjects.includes(project.id);
                        const projectScriptsCount = project.scripts?.length || 0;

                        return (
                            <Card key={project.id} size="small">
                                <Space>
                                    <Checkbox
                                        checked={isProjectSelected}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            handleProjectChange(project.id, e.target.checked);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        disabled={isSyncing}
                                    />
                                    <strong>{project.title}</strong>
                                    <Tag color={isProjectSelected ? 'blue' : 'default'}>Скриптів в проекті: {projectScriptsCount}</Tag>
                                </Space>
                            </Card>
                        );
                    })}

                    {filteredProjects.length === 0 && <Empty description="Проекти не знайдено" />}
                </Space>
            </div>
        </Space>
    );
});
